import { t } from '@lingui/macro'
import Tooltip from '@mui/material/Tooltip'
import { Check, Copy } from 'lucide-react'
import React, { useCallback, useEffect, useState } from 'react'

interface CopyableIdentifierProps {
    /** The identifier string to display and copy. If null/undefined, renders an em-dash. */
    identifier: string | null | undefined
    /** Optional candidate cross-reference ID shown as secondary text. */
    candidateIdentifier?: string | null
}

/**
 * Displays a human-friendly identifier with a one-click copy button.
 * - Monospace font for readability
 * - Copy icon toggles to checkmark for 2 seconds on success
 * - Hidden gracefully if clipboard API is unavailable
 * - Accessible with aria-label
 */
export const CopyableIdentifier: React.FC<CopyableIdentifierProps> = ({ identifier, candidateIdentifier }) => {
    const [copied, setCopied] = useState(false)
    const [clipboardAvailable, setClipboardAvailable] = useState(true)

    useEffect(() => {
        if (!navigator?.clipboard?.writeText) {
            setClipboardAvailable(false)
        }
    }, [])

    const handleCopy = useCallback(async () => {
        if (!identifier) return
        try {
            await navigator.clipboard.writeText(identifier)
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
        } catch {
            // Silently fail — clipboard may be blocked
        }
    }, [identifier])

    if (!identifier) {
        return <span className='font-mono text-sm text-gray-400'>{'—'}</span>
    }

    return (
        <span className='inline-flex items-center gap-1 whitespace-nowrap'>
            <span className='font-mono text-sm'>{identifier}</span>
            {clipboardAvailable && (
                <Tooltip title={copied ? t`Copied!` : t`Copy identifier`}>
                    <button
                        type='button'
                        onClick={handleCopy}
                        aria-label={t`Copy identifier ${identifier} to clipboard`}
                        className='inline-flex items-center justify-center p-0.5 rounded hover:bg-gray-100 transition-colors'
                    >
                        {copied ? <Check size={14} className='text-green-600' /> : <Copy size={14} className='text-gray-400 hover:text-gray-600' />}
                    </button>
                </Tooltip>
            )}
            {candidateIdentifier && (
                <Tooltip title={t`Original candidate identifier`}>
                    <span className='font-mono text-xs text-gray-400 ml-1'>({candidateIdentifier})</span>
                </Tooltip>
            )}
        </span>
    )
}
